import React, { useState, useEffect } from "react";
import { TrashIcon, PlusIcon, DocumentIcon } from "@heroicons/react/24/outline";
import { ChevronRight, ChevronDown } from "lucide-react";
import VariableInput from "../Variables/VariableInput";
import { useRequestData } from "../../context/RequestDataContext";

const JsonNode = ({ keyName, value, depth = 0, isLast = true }) => {
  const [collapsed, setCollapsed] = useState(depth > 1);

  const isObject = value !== null && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isCollapsible = isObject || isArray;
  const bracketOpen = isArray ? "[" : "{";
  const bracketClose = isArray ? "]" : "}";
  const childEntries = isCollapsible ? Object.entries(value) : [];

  const renderPrimitive = (val) => {
    if (val === null) return <span className="text-gray-400 font-mono text-xs">null</span>;
    if (typeof val === "boolean")
      return <span className="text-purple-600 font-mono text-xs">{val.toString()}</span>;
    if (typeof val === "number")
      return <span className="text-blue-600 font-mono text-xs">{val}</span>;
    if (typeof val === "string")
      return <span className="text-green-700 font-mono text-xs">"{val}"</span>;
    return null;
  };

  return (
    <div style={{ paddingLeft: depth === 0 ? 0 : 16 }}>
      <div className="flex items-center hover:bg-blue-50 rounded group min-h-[22px]">
        <span
          className="inline-flex items-center justify-center flex-shrink-0"
          style={{ width: 16 }}
        >
          {isCollapsible && (
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
            >
              {collapsed ? <ChevronRight size={11} /> : <ChevronDown size={11} />}
            </button>
          )}
        </span>

        {keyName !== undefined && (
          <span className="font-mono text-xs flex-shrink-0">
            <span className="text-red-600">"{keyName}"</span>
            <span className="text-gray-500">:&nbsp;</span>
          </span>
        )}

        {isCollapsible ? (
          <span className="font-mono text-xs text-gray-600">
            {bracketOpen}
            {collapsed && (
              <>
                <span
                  className="text-gray-400 mx-1 cursor-pointer hover:text-blue-500"
                  onClick={() => setCollapsed(false)}
                >
                  {childEntries.length} {childEntries.length === 1 ? "item" : "items"}
                </span>
                {bracketClose}
                {!isLast && <span className="text-gray-500">,</span>}
              </>
            )}
          </span>
        ) : (
          <span className="font-mono text-xs">
            {renderPrimitive(value)}
            {!isLast && <span className="text-gray-500">,</span>}
          </span>
        )}
      </div>

      {isCollapsible && !collapsed && (
        <>
          {childEntries.map(([k, v], i) => (
            <JsonNode
              key={k}
              keyName={isArray ? undefined : k}
              value={v}
              depth={depth + 1}
              isLast={i === childEntries.length - 1}
            />
          ))}
          <div className="flex items-center min-h-[22px]">
            <span className="inline-flex flex-shrink-0" style={{ width: 16 }} />
            <span className="font-mono text-xs text-gray-600">
              {bracketClose}
              {!isLast && <span className="text-gray-500">,</span>}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

const BodyEditor = ({ className = "", onBodyTypeChange }) => {
  const {
    bodyType,
    setBodyType,
    bodySubType,
    setBodySubType,
    bodyContentByType,
    updateBodyContent,
    getBodyContent,
    formFields,
    setFormFields,
    urlEncodedFields,
    setUrlEncodedFields,
    getCurrentSourceData,
  } = useRequestData();

  const [activeBodyType, setActiveBodyType] = useState(bodyType);

  const [jsonViewMode, setJsonViewMode] = useState("tree");
  const [jsonParseError, setJsonParseError] = useState(null);
  const [parsedJsonTree, setParsedJsonTree] = useState(null);
  const [treeKey, setTreeKey] = useState(0);

  useEffect(() => {
    setActiveBodyType(bodyType);
    if (bodyType !== "json") {
      setJsonViewMode("code");
      setJsonParseError(null);
      setParsedJsonTree(null);
    } else {
      try {
        const parsed = JSON.parse(getBodyContent("json"));
        setParsedJsonTree(parsed);
        setTreeKey((k) => k + 1);
        setJsonParseError(null);
        setJsonViewMode("tree");
      } catch {
        setJsonViewMode("code");
      }
    }
  }, [bodyType]);

  useEffect(() => {
    if (jsonViewMode === "tree") {
      try {
        const parsed = JSON.parse(getBodyContent("json"));
        setParsedJsonTree(parsed);
        setTreeKey((k) => k + 1);
        setJsonParseError(null);
      } catch {
        setJsonViewMode("code");
        setJsonParseError(null);
      }
    }
  }, [bodyContentByType, jsonViewMode, getBodyContent]);

  const handleSwitchToTree = () => {
    try {
      const parsed = JSON.parse(getBodyContent("json"));
      setParsedJsonTree(parsed);
      setJsonParseError(null);
      setJsonViewMode("tree");
    } catch (e) {
      setJsonParseError("Invalid JSON — fix errors before switching to Tree view.");
    }
  };

  const handleSwitchToCode = () => {
    setJsonViewMode("code");
    setJsonParseError(null);
  };

  const handleResetCollapse = () => {
    setTreeKey((k) => k + 1);
  };

  const handleBodyTypeChange = (newType) => {
    setActiveBodyType(newType);
    setBodyType(newType);

    let subType = null;
    if (newType === "form-data") {
      const hasFileField = formFields.some((f) => f.type === "file");
      subType = hasFileField ? "file" : "text";
      setBodySubType(subType);
    } else if (newType === "json") {
      subType = "json";
    }

    if (onBodyTypeChange) {
      onBodyTypeChange(newType, subType);
    }
  };

  const handleContentChange = (content) => {
    updateBodyContent(activeBodyType, content);
  };

  const addFormField = () => {
    const newFields = [
      ...formFields,
      {
        key: "",
        value: "",
        type: "text",
        src: null,
        contentType: null,
        description: null,
        required: false,
        fileObject: null,
      },
    ];
    setFormFields(newFields);
    updateFormContent(newFields);
  };

  const removeFormField = (index) => {
    const newFields = formFields.filter((_, i) => i !== index);
    setFormFields(newFields);
    updateFormContent(newFields);
  };

  const updateFormField = (index, field, value) => {
    const newFields = [...formFields];
    newFields[index][field] = value;
    setFormFields(newFields);
    updateFormContent(newFields);

    if (field === "type") {
      const hasFileField = newFields.some((f) => f.type === "file");
      const newSubType = hasFileField ? "file" : "text";
      setBodySubType(newSubType);

      if (onBodyTypeChange) {
        onBodyTypeChange("form-data", newSubType);
      }
    }
  };

  const updateFormContent = (fields) => {
    const formattedFields = fields.map((field) => ({
      key: field.key,
      value: field.value,
      type: field.type,
      contentType: field.contentType || null,
      description: field.description || null,
      required: field.required || false,
      src: field.type === "file" ? field.src : null,
      fileObject: field.type === "file" ? field.fileObject : null,
    }));

    const formDataObject = { fields: formattedFields };
    const jsonContent = JSON.stringify(formDataObject);
    updateBodyContent("form-data", jsonContent);
  };

  const addUrlField = () => {
    const newFields = [...urlEncodedFields, { key: "", value: "" }];
    setUrlEncodedFields(newFields);
    updateUrlContent(newFields);
  };

  const removeUrlField = (index) => {
    const newFields = urlEncodedFields.filter((_, i) => i !== index);
    setUrlEncodedFields(newFields);
    updateUrlContent(newFields);
  };

  const updateUrlField = (index, field, value) => {
    const newFields = [...urlEncodedFields];
    newFields[index][field] = value;
    setUrlEncodedFields(newFields);
    updateUrlContent(newFields);
  };

  const updateUrlContent = (fields) => {
    const urlEncoded = fields
      .filter((field) => field.key)
      .map((field) => `${encodeURIComponent(field.key)}=${encodeURIComponent(field.value)}`)
      .join("&");
    updateBodyContent("form-urlencoded", urlEncoded);
  };

  const handleFileSelect = (file) => {
    if (file) {
      const fileContent = `[File: ${file.name}]`;
      updateBodyContent("binary", fileContent);
      updateBodyContent("binary", fileContent, file);
    }
  };

  const getPlaceholder = () => {
    switch (activeBodyType) {
      case "json":
        return '{\n  "key": "value"\n}';
      case "xml":
        return '<?xml version="1.0"?>\n<root>\n  <item>value</item>\n</root>';
      case "text":
        return "Enter plain text here...";
      default:
        return "";
    }
  };

  const calculateTextareaHeight = (value) => {
    if (!value) return 40;
    const lines = value.split("\n").length;
    return Math.max(40, Math.min(lines * 20 + 16, 200));
  };

  const renderBodyContent = () => {
    switch (activeBodyType) {
      case "none":
        return (
          <div className="flex items-center justify-center h-full bg-gray-50 text-gray-500">
            This request does not have a body
          </div>
        );

      case "json":
        return (
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-1 bg-white border border-gray-200 rounded p-0.5">
                <button
                  onClick={handleSwitchToCode}
                  className={`px-3 py-0.5 text-xs font-medium rounded transition-colors cursor-pointer font-apple ${
                    jsonViewMode === "code"
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Code
                </button>
                <button
                  onClick={handleSwitchToTree}
                  className={`px-3 py-0.5 text-xs font-medium rounded transition-colors cursor-pointer font-apple ${
                    jsonViewMode === "tree"
                      ? "bg-blue-600 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Tree
                </button>
              </div>

              {jsonViewMode === "tree" && (
                <button
                  onClick={handleResetCollapse}
                  className="text-xs text-gray-500 hover:text-gray-700 font-apple transition-colors cursor-pointer"
                >
                  Reset collapse
                </button>
              )}

              {jsonParseError && (
                <span className="text-xs text-red-500 font-apple">{jsonParseError}</span>
              )}
            </div>

            <div className="flex-1 overflow-auto h-full">
              {jsonViewMode === "code" ? (
                <VariableInput
                  value={getBodyContent("json")}
                  onChange={handleContentChange}
                  className="w-full h-full p-4 text-sm border-0 focus:outline-none resize-none bg-white font-mono"
                  placeholder={getPlaceholder()}
                  multiline={true}
                  showAutocomplete={true}
                />
              ) : (
                <div key={treeKey} className="p-4 bg-white min-h-full">
                  {parsedJsonTree !== null ? (
                    <JsonNode value={parsedJsonTree} depth={0} isLast={true} />
                  ) : (
                    <span className="text-xs text-gray-400">Nothing to display.</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case "xml":
      case "text":
        return (
          <div className="h-full w-full">
            <VariableInput
              value={getBodyContent(activeBodyType)}
              onChange={handleContentChange}
              className="w-full h-full p-4 text-sm border-0 focus:outline-none resize-none bg-white font-mono"
              placeholder={getPlaceholder()}
              multiline={true}
              showAutocomplete={true}
            />
          </div>
        );

      case "form-data":
        return (
          <div className="h-full overflow-auto p-4 bg-white">
            <div className="space-y-3">
              {formFields.map((field, index) => (
                <div key={index} className="flex flex-col gap-2 p-3 border border-gray-200 rounded">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Key"
                      value={field.key}
                      onChange={(e) => updateFormField(index, "key", e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 font-mono"
                    />

                    <select
                      value={field.type}
                      onChange={(e) => updateFormField(index, "type", e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                    >
                      <option value="text">Text</option>
                      <option value="file">File</option>
                    </select>

                    <button
                      onClick={() => removeFormField(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      disabled={formFields.length === 1}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {field.type === "file" ? (
                    <div className="w-full">
                      <input
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            updateFormField(index, "value", file.name);
                            updateFormField(index, "src", file.path || file.name);
                            updateFormField(index, "fileObject", file);
                            updateFormField(index, "contentType", file.type);
                          }
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                      />
                      {field.src && (
                        <div className="text-xs text-gray-500 mt-1 px-1 break-all">
                          Path: {field.src}
                        </div>
                      )}
                    </div>
                  ) : (
                    <textarea
                      value={field.value}
                      onChange={(e) => updateFormField(index, "value", e.target.value)}
                      placeholder="Value"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 resize-none font-mono"
                      style={{ minHeight: calculateTextareaHeight(field.value) }}
                    />
                  )}
                </div>
              ))}

              <button
                onClick={addFormField}
                className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded"
              >
                <PlusIcon className="h-4 w-4" />
                Add Field
              </button>
            </div>
          </div>
        );

      case "form-urlencoded":
        return (
          <div className="h-full overflow-auto p-4 bg-white">
            <div className="space-y-3">
              {urlEncodedFields.map((field, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-3 border border-gray-200 bg-white rounded transition-colors"
                >
                  <div className="flex-1 flex gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Key"
                        value={field.key}
                        onChange={(e) => updateUrlField(index, "key", e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 font-mono"
                      />
                    </div>
                    <div className="flex-1">
                      <VariableInput
                        value={field.value}
                        onChange={(value) => updateUrlField(index, "value", value)}
                        placeholder="Value"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 font-mono"
                        showAutocomplete={true}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => removeUrlField(index)}
                    className={`p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded ${
                      urlEncodedFields.length === 1
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    disabled={urlEncodedFields.length === 1}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <button
                onClick={addUrlField}
                className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded"
              >
                <PlusIcon className="h-4 w-4" />
                Add Field
              </button>

              {/* Preview */}
              <div className="mt-4 p-3 bg-gray-50 border rounded">
                <div className="text-xs font-medium text-gray-600 mb-2">Preview:</div>
                <div className="text-xs font-mono text-gray-800 break-all">
                  {urlEncodedFields
                    .filter((field) => field.key)
                    .map(
                      (field) =>
                        `${encodeURIComponent(field.key)}=${encodeURIComponent(field.value)}`,
                    )
                    .join("&") || "No data"}
                </div>
              </div>
            </div>
          </div>
        );

      case "binary":
        return (
          <div className="h-full flex items-center justify-center p-8">
            <div className="text-center">
              <input
                type="file"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                id="binary-file"
              />
              <label
                htmlFor="binary-file"
                className="flex flex-col items-center gap-4 p-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50"
              >
                <DocumentIcon className="h-12 w-12 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-700">
                    {getBodyContent("binary") || "Choose a file"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Click to select a binary file</div>
                </div>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="flex border-b border-gray-200 bg-gray-50">
        {[
          { id: "none", label: "None" },
          { id: "json", label: "JSON" },
          { id: "xml", label: "XML" },
          { id: "text", label: "Text" },
          { id: "form-data", label: "Form Data" },
          { id: "form-urlencoded", label: "Form Encoded" },
          { id: "binary", label: "Binary" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleBodyTypeChange(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-r cursor-pointer border-gray-200 transition-colors ${
              activeBodyType === tab.id
                ? "bg-blue-600 text-white"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-hidden">{renderBodyContent()}</div>

      {activeBodyType !== "none" && (
        <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 text-xs text-gray-600">
          Content-Type:{" "}
          {activeBodyType === "json"
            ? "application/json"
            : activeBodyType === "xml"
              ? "application/xml"
              : activeBodyType === "text"
                ? "text/plain"
                : activeBodyType === "form-data"
                  ? "multipart/form-data"
                  : activeBodyType === "form-urlencoded"
                    ? "application/x-www-form-urlencoded"
                    : activeBodyType === "binary"
                      ? "application/octet-stream"
                      : "text/plain"}
        </div>
      )}
    </div>
  );
};

export default BodyEditor;
